import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMe } from "../../api/hooks";
import {
  fetchInboxItem,
  type InboxFilter,
  type InboxItem,
  type InboxPage,
  type InboxPages,
  inboxKeys,
  isInboxFilter,
  itemsOf,
  markReadInPages,
  markReadInSummary,
  readInboxItem,
  useInboxList,
} from "../../api/inbox";
import { InboxReader } from "../../components/inbox/InboxReader";
import { inboxNav } from "../../components/inbox/listNav";
import { BackButton } from "../../components/shell/Page";
import { Icon } from "../../components/ui/primitives";
import { t } from "../../i18n";

type Search = { filter?: InboxFilter; item?: string };

export const Route = createFileRoute("/_app/inbox")({
  // `filter` picks a chip; `item` is the one open in the reading pane (a phone shows it alone,
  // and Back returns to the list).
  validateSearch: (search: Record<string, unknown>): Search => ({
    ...(isInboxFilter(search.filter) && search.filter !== "all" ? { filter: search.filter } : {}),
    ...(typeof search.item === "string" && search.item ? { item: search.item } : {}),
  }),
  component: Inbox,
});

/** The Inbox (PLAN §8.6; mockup `#inbox`): what Vantage brought you, newest first. R7.5: the
 *  list and its filter chips live in the shell's Inbox column (components/shell/Lists.tsx); the
 *  page is the reading pane beside it (over it on phones). Opening an item marks it read. */
function Inbox() {
  const me = useMe();
  const { filter = "all", item: openId } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // The column's own query (same key): the open item is found in what it lists.
  const list = useInboxList(filter);
  const items = itemsOf(list.data);
  // The open item, kept when it leaves the list (read under "Unread", or on a later page).
  const [kept, setKept] = useState<InboxItem | null>(null);
  const found = items.find((i) => i.id === openId);
  useEffect(() => {
    if (found) setKept(found);
  }, [found]);
  // A link to one the loaded pages don't hold (R10-N7): asked for alone.
  const single = useQuery({
    queryKey: inboxKeys.item(openId ?? ""),
    enabled: Boolean(openId) && list.isSuccess && !found && kept?.id !== openId,
    queryFn: () => fetchInboxItem(openId ?? ""),
  });
  const open =
    found ??
    (kept?.id === openId ? kept : null) ??
    (single.data?.id === openId ? single.data : null);

  // Reading an item marks it read: here at once (the dot, the counts, the rail's badge), then
  // on the server, whose `read.updated` refreshes every inbox query.
  const marked = useRef(new Set<string>());
  useEffect(() => {
    if (!open || open.read_at || marked.current.has(open.id)) return;
    marked.current.add(open.id);
    const at = new Date().toISOString();
    qc.setQueriesData<InboxPages>({ queryKey: ["inbox", "list"] }, (p) =>
      markReadInPages(p, open.id, at),
    );
    qc.setQueryData<InboxPage>(inboxKeys.summary, (p) => markReadInSummary(p, true));
    // Once per item: a failure shows the truth again (the refetch), and isn't retried in a loop.
    const refresh = () => void qc.invalidateQueries({ queryKey: inboxKeys.all });
    void readInboxItem(open.id).then(refresh, refresh);
  }, [open, qc]);

  // "Opened from the list" holds for this visit of the page only: once it closes, a later Back
  // must never run the browser's Back to somewhere else.
  useEffect(() => {
    return () => {
      inboxNav.fromList = null;
    };
  }, []);

  return (
    <main className="sub main on no-panel" aria-label={t("inbox.readingPane")}>
      {open && me.data ? (
        <InboxReader
          key={open.id}
          item={open}
          me={me.data}
          onBack={() => {
            // Opened from the list: going back is the browser's Back. Opened by a link: the
            // list replaces the item.
            if (inboxNav.fromList === open.id) {
              inboxNav.fromList = null;
              window.history.back();
            } else
              void navigate({
                to: "/inbox",
                search: filter !== "all" ? { filter } : {},
                replace: true,
              });
          }}
        />
      ) : (
        <div className="ib-view ib-none">
          {/* A phone that followed a link to an item no longer listed: back to the list. */}
          {openId && !list.isLoading && (
            <BackButton
              onBack={() =>
                void navigate({
                  to: "/inbox",
                  search: filter !== "all" ? { filter } : {},
                  replace: true,
                })
              }
            />
          )}
          <div className="empty">
            <Icon name="i-inbox" size="l" />
            <p>{openId && list.isLoading ? t("common.loading") : t("inbox.pickItem")}</p>
          </div>
        </div>
      )}
    </main>
  );
}
